import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, AlertTriangle, Trash2, Brain, Activity } from "lucide-react";
import { toast } from "sonner";

const PHQ9_QUESTIONS = [
  "物事に対してほとんど興味がない、または楽しめない",
  "気分が落ち込む、憂うつになる、または絶望的な気持ちになる",
  "寝付きが悪い、途中で目が覚める、または逆に眠り過ぎる",
  "疲れた感じがする、または気力がない",
  "あまり食欲がない、または食べ過ぎる",
  "自分はダメな人間だ、人生の敗北者だと気に病む、または自分自身あるいは家族に申し訳がないと感じる",
  "新聞を読む、またはテレビを見ることなどに集中することが難しい",
  "他人が気づくぐらいに動きや話し方が遅くなる、あるいは反対に、そわそわしたり、落ち着かず、ふだんよりも動き回ることがある",
  "死んだ方がましだ、あるいは自分を何らかの方法で傷つけようと思ったことがある",
];
const PHQ9_OPTIONS = [
  { v: 0, l: "全くない" },
  { v: 1, l: "数日" },
  { v: 2, l: "半分以上" },
  { v: 3, l: "ほとんど毎日" },
];

const BARTHEL_ITEMS = [
  { k: "food", l: "食事", opts: [[10, "自立"], [5, "部分介助"], [0, "全介助"]] },
  { k: "transfer", l: "移乗 (ベッド⇔車椅子)", opts: [[15, "自立"], [10, "最小介助"], [5, "部分介助"], [0, "全介助"]] },
  { k: "grooming", l: "整容", opts: [[5, "自立"], [0, "介助"]] },
  { k: "toilet", l: "トイレ動作", opts: [[10, "自立"], [5, "部分介助"], [0, "全介助"]] },
  { k: "bathing", l: "入浴", opts: [[5, "自立"], [0, "介助"]] },
  { k: "mobility", l: "歩行", opts: [[15, "自立(45m)"], [10, "歩行補助具使用"], [5, "車椅子自走"], [0, "全介助"]] },
  { k: "stairs", l: "階段昇降", opts: [[10, "自立"], [5, "介助"], [0, "不能"]] },
  { k: "dressing", l: "更衣", opts: [[10, "自立"], [5, "部分介助"], [0, "全介助"]] },
  { k: "bowel", l: "排便", opts: [[10, "自立"], [5, "失敗ときどき"], [0, "失禁"]] },
  { k: "bladder", l: "排尿", opts: [[10, "自立"], [5, "失敗ときどき"], [0, "失禁"]] },
];

export default function Evaluations() {
  const [patients, setPatients] = useState([]);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("phq9");
  const [patientFilter, setPatientFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get("type");
    if (t && ["phq9", "gaf", "barthel"].includes(t)) setType(t);
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      searchParams.delete("type");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = () => {
    api.get("/evaluations", { params: { eval_type: type, patient_id: patientFilter || undefined } }).then((r) => setItems(r.data));
  };
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)); }, []);
  useEffect(load, [type, patientFilter]);

  const patientName = (pid) => patients.find((p) => p.id === pid)?.name_kanji || "?";
  const del = async (id) => { if (!confirm("削除しますか?")) return; await api.delete(`/evaluations/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="evaluations-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">EVALUATIONS</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">評価ツール</h1>
        <p className="text-[#4A4A48] mt-1">GAF · PHQ-9(うつ病チェック) · バーセル指数 · DESIGN-R</p>
      </div>

      <Tabs value={type} onValueChange={setType}>
        <TabsList className="bg-white border border-[#E5E5E3] h-auto flex-wrap">
          <TabsTrigger value="phq9" data-testid="tab-phq9"><Brain size={14} className="mr-1" />PHQ-9 うつ病</TabsTrigger>
          <TabsTrigger value="gaf" data-testid="tab-gaf"><Activity size={14} className="mr-1" />GAF</TabsTrigger>
          <TabsTrigger value="barthel" data-testid="tab-barthel">バーセル指数</TabsTrigger>
          <TabsTrigger value="design_r" data-testid="tab-design">DESIGN-R</TabsTrigger>
        </TabsList>

        <div className="flex items-center justify-between gap-4 mt-4">
          <select value={patientFilter} onChange={(e) => setPatientFilter(e.target.value)} className="flex h-10 rounded-md border border-input px-3 text-sm bg-white">
            <option value="">全ての利用者</option>
            {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
          </select>
          <NewEvalDialog type={type} patients={patients} open={open} setOpen={setOpen} onSaved={load} />
        </div>

        <TabsContent value="phq9" className="mt-4"><EvalList items={items} type="phq9" patientName={patientName} onDel={del} /></TabsContent>
        <TabsContent value="gaf" className="mt-4"><EvalList items={items} type="gaf" patientName={patientName} onDel={del} /></TabsContent>
        <TabsContent value="barthel" className="mt-4"><EvalList items={items} type="barthel" patientName={patientName} onDel={del} /></TabsContent>
        <TabsContent value="design_r" className="mt-4"><EvalList items={items} type="design_r" patientName={patientName} onDel={del} /></TabsContent>
      </Tabs>
    </div>
  );
}

function EvalList({ items, type, patientName, onDel }) {
  if (items.length === 0) return <div className="text-center py-12 text-[#8A8A86]">記録がありません</div>;
  return (
    <div className="space-y-3">
      {items.map((e) => (
        <Card key={e.id} className={`p-4 border-[#E5E5E3] ${e.suicide_risk ? "border-[#A74141]/40 bg-[#FDF5F5]" : ""}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{patientName(e.patient_id)}</span>
                <Badge variant="outline" className="font-numeric">{e.eval_date}</Badge>
                {e.suicide_risk && <Badge className="bg-[#A74141] text-white"><AlertTriangle size={12} className="mr-1" />希死念慮あり</Badge>}
              </div>
              <div className="flex items-baseline gap-4 mt-2">
                <div className="text-3xl font-bold font-numeric text-[#2C5545]">{e.score}</div>
                <div className="text-sm text-[#4A4A48]">{e.severity}</div>
              </div>
              {e.note && <div className="text-sm text-[#4A4A48] mt-2 whitespace-pre-wrap">{e.note}</div>}
            </div>
            <button onClick={() => onDel(e.id)} className="text-[#A74141]"><Trash2 size={14} /></button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function NewEvalDialog({ type, patients, open, setOpen, onSaved }) {
  const [form, setForm] = useState(() => init(type));
  useEffect(() => { setForm(init(type)); }, [type, open]);

  function init(t) {
    const base = { patient_id: "", eval_date: new Date().toISOString().slice(0, 10), note: "" };
    if (t === "phq9") return { ...base, answers: Object.fromEntries(PHQ9_QUESTIONS.map((_, i) => [`q${i + 1}`, 0])) };
    if (t === "gaf") return { ...base, score: 50 };
    if (t === "barthel") return { ...base, answers: Object.fromEntries(BARTHEL_ITEMS.map((x) => [x.k, 0])) };
    if (t === "design_r") return { ...base, score: 0, answers: { depth: 0, exudate: 0, size: 0, inflammation: 0, granulation: 0, necrotic: 0, pocket: 0 } };
    return base;
  }

  const save = async () => {
    if (!form.patient_id) { toast.error("利用者を選択してください"); return; }
    await api.post("/evaluations", { ...form, eval_type: type });
    toast.success("評価を保存しました");
    setOpen(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-eval-button" className="bg-[#2C5545] text-white"><Plus size={16} className="mr-2" />新規評価</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{labelOf(type)} 新規評価</DialogTitle></DialogHeader>
        <div className="space-y-4 py-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>利用者<span className="text-[#A74141]">*</span></Label>
              <select value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                <option value="">選択</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name_kanji}</option>)}
              </select>
            </div>
            <div><Label>評価日</Label><Input type="date" value={form.eval_date} onChange={(e) => setForm({ ...form, eval_date: e.target.value })} /></div>
          </div>

          {type === "phq9" && <PHQ9Form form={form} setForm={setForm} />}
          {type === "gaf" && <GAFForm form={form} setForm={setForm} />}
          {type === "barthel" && <BarthelForm form={form} setForm={setForm} />}
          {type === "design_r" && <DesignRForm form={form} setForm={setForm} />}

          <div><Label>所見・備考</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
          <Button data-testid="save-eval-button" className="bg-[#2C5545] text-white" onClick={save}>保存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PHQ9Form({ form, setForm }) {
  const total = Object.values(form.answers).reduce((a, b) => a + Number(b), 0);
  const q9 = Number(form.answers.q9 || 0);
  const severity =
    total <= 4 ? "なし(最小)" : total <= 9 ? "軽度" : total <= 14 ? "中等度" : total <= 19 ? "中等度〜重度" : "重度";
  return (
    <div className="space-y-3">
      <div className="text-sm text-[#4A4A48]">過去2週間について、各項目をどのくらいの頻度で感じましたか?</div>
      {PHQ9_QUESTIONS.map((q, i) => (
        <Card key={i} className={`p-3 border-[#E5E5E3] ${i === 8 && q9 >= 1 ? "border-[#A74141] bg-[#FDF5F5]" : ""}`}>
          <div className="text-sm mb-2">{i + 1}. {q}</div>
          <div className="flex gap-2 flex-wrap">
            {PHQ9_OPTIONS.map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`phq9-q${i + 1}-${o.v}`}
                onClick={() => setForm({ ...form, answers: { ...form.answers, [`q${i + 1}`]: o.v } })}
                className={`px-3 py-1 text-xs rounded border transition ${
                  Number(form.answers[`q${i + 1}`]) === o.v
                    ? "bg-[#2C5545] text-white border-[#2C5545]"
                    : "bg-white border-[#E5E5E3]"
                }`}
              >
                {o.v} · {o.l}
              </button>
            ))}
          </div>
        </Card>
      ))}
      <Card className="p-4 bg-[#D9E2D5] border-[#2C5545]/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#2C5545]">合計スコア</div>
            <div className="text-3xl font-bold font-numeric text-[#2C5545]">{total} / 27</div>
            <div className="text-sm text-[#4A4A48]">重症度: <strong>{severity}</strong></div>
          </div>
          {q9 >= 1 && (
            <div className="flex items-center gap-2 text-[#A74141]">
              <AlertTriangle size={20} />
              <div className="text-sm font-bold">希死念慮あり<br />主治医への即時連絡推奨</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function GAFForm({ form, setForm }) {
  return (
    <div className="space-y-3">
      <div className="text-sm text-[#4A4A48]">1〜100点の範囲で総合機能評定 (GAF)</div>
      <div className="flex items-center gap-4">
        <Input type="range" min="1" max="100" value={form.score} onChange={(e) => setForm({ ...form, score: Number(e.target.value) })} className="flex-1" data-testid="gaf-slider" />
        <div className="text-3xl font-bold font-numeric w-20 text-center text-[#2C5545]">{form.score}</div>
      </div>
      <div className="text-xs text-[#8A8A86] grid grid-cols-2 gap-1 font-numeric">
        <div>91-100: 優秀な機能</div>
        <div>81-90: 症状なし・最小</div>
        <div>71-80: 一過性の症状</div>
        <div>61-70: 軽度症状</div>
        <div>51-60: 中等度症状</div>
        <div>41-50: 重篤な症状</div>
        <div>31-40: 現実検討の障害</div>
        <div>21-30: 妄想・幻覚の影響</div>
        <div>11-20: 自傷・他害の危険</div>
        <div>1-10: 自殺企図の明らかな危険</div>
      </div>
    </div>
  );
}

function BarthelForm({ form, setForm }) {
  const total = Object.values(form.answers).reduce((a, b) => a + Number(b), 0);
  return (
    <div className="space-y-3">
      {BARTHEL_ITEMS.map((it) => (
        <Card key={it.k} className="p-3 border-[#E5E5E3]">
          <div className="text-sm font-medium mb-2">{it.l}</div>
          <div className="flex gap-2 flex-wrap">
            {it.opts.map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setForm({ ...form, answers: { ...form.answers, [it.k]: v } })}
                className={`px-3 py-1 text-xs rounded border ${
                  Number(form.answers[it.k]) === v ? "bg-[#2C5545] text-white border-[#2C5545]" : "bg-white border-[#E5E5E3]"
                }`}
              >
                {v}点 · {l}
              </button>
            ))}
          </div>
        </Card>
      ))}
      <Card className="p-4 bg-[#D9E2D5]">
        <div className="text-xs uppercase tracking-wider text-[#2C5545]">合計</div>
        <div className="text-3xl font-bold font-numeric text-[#2C5545]">{total} / 100</div>
      </Card>
    </div>
  );
}

function DesignRForm({ form, setForm }) {
  const items = [
    { k: "depth", l: "Depth 深さ (0-4)", max: 4 },
    { k: "exudate", l: "Exudate 滲出液 (0-3)", max: 3 },
    { k: "size", l: "Size 大きさ (0-15)", max: 15 },
    { k: "inflammation", l: "Inflammation 炎症 (0-3)", max: 3 },
    { k: "granulation", l: "Granulation 肉芽 (0-6)", max: 6 },
    { k: "necrotic", l: "Necrotic 壊死 (0-6)", max: 6 },
    { k: "pocket", l: "Pocket ポケット (0-24)", max: 24 },
  ];
  const total = Object.values(form.answers).reduce((a, b) => a + Number(b), 0);
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.k}>
          <Label className="text-sm">{it.l}</Label>
          <Input type="number" min="0" max={it.max} value={form.answers[it.k]} onChange={(e) => setForm({ ...form, answers: { ...form.answers, [it.k]: Number(e.target.value) } })} />
        </div>
      ))}
      <Card className="p-4 bg-[#D9E2D5]">
        <div className="text-xs uppercase tracking-wider text-[#2C5545]">DESIGN-R 合計</div>
        <div className="text-3xl font-bold font-numeric text-[#2C5545]">{total}</div>
      </Card>
    </div>
  );
}

function labelOf(t) {
  return { phq9: "PHQ-9 うつ病スクリーニング", gaf: "GAF 機能の全体的評定", barthel: "バーセル指数", design_r: "DESIGN-R 褥瘡評価" }[t] || t;
}
